import * as React from 'react';
import { useEffect, useState, useMemo } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { useQuery } from 'react-query';
import Avatar from '@mui/material/Avatar';
import apiClient from '../../request/http-common';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { Button, TextField, Box, InputAdornment, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { Link as RouterLink } from 'react-router-dom';

export default function ListUsers() {
    const [users, setUsers] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const { isLoading: isLoadingusers, refetch: getusers } = useQuery(
        "load-users",
        async () => {
          return await apiClient.get("/api/users");
        },
        {
          enabled: false,
          onSuccess: (res) => {
            if (res.data.status === 'success') {
                setUsers(res.data.users);
                const event2 = new Event('processed');
                window.dispatchEvent(event2);
            }
          },
          onError: (err) => {
            // console.log(err.response?.data || err);
          },
        }
      );
    
      // Optimized search filtering with useMemo
      const filteredUsersMemo = useMemo(() => {
        if (!users) return [];
        if (!searchTerm.trim()) return users;
        
        const searchLower = searchTerm.toLowerCase();
        return users.filter(user => 
          user.name?.toLowerCase().includes(searchLower) ||
          user.email?.toLowerCase().includes(searchLower) ||
          user.phone?.toLowerCase().includes(searchLower) ||
          user.country?.toLowerCase().includes(searchLower) ||
          user.currency?.toLowerCase().includes(searchLower)
        );
      }, [users, searchTerm]);

      // Search filtering function
      const handleSearch = (searchValue) => {
        setSearchTerm(searchValue);
      };

      // Clear search function
      const clearSearch = () => {
        setSearchTerm('');
      };

      useEffect(() => {
        getusers() 
        if (isLoadingusers) {
            const event3 = new Event('processing');
            window.dispatchEvent(event3);
        }
      }, []);

  const getDate = (date) => new Date(date).toDateString();
  return (
    <div style={{marginTop: '75px'}}>
    <Container>
        <Typography  mt={4} mb={2} sx={{ fontWeight: 'bold', fontSize: 20, textAlign: 'center', textTransform: 'uppercase' }} gutterBottom variant="h6" component="div">
        
        {isLoadingusers? 'Loading Users...' : 'List of users'}
      </Typography>
      
      {/* Search Input */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
        <TextField
          placeholder="Search users by name, email, phone, country, or currency..."
          value={searchTerm}
          onChange={(e) => handleSearch(e.target.value)}
          sx={{ 
            width: '100%', 
            maxWidth: 600,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton
                  onClick={clearSearch}
                  edge="end"
                  size="small"
                  aria-label="clear search"
                >
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
          variant="outlined"
        />
      </Box>
    <TableContainer component={Paper}>
        
      {users !== null && (
        <>
          {/* Results Counter */}
          <Box sx={{ mb: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {searchTerm ? 
                `Showing ${filteredUsersMemo.length} of ${users?.length || 0} users` : 
                `Total: ${filteredUsersMemo.length} users`
              }
            </Typography>
          </Box>
          
          <Table sx={{ minWidth: '100%' }} size="small" aria-label="a dense table">
      <TableHead>
        <TableRow>
        <TableCell sx={{ fontWeight: 'bold' }}>User Name</TableCell>
          <TableCell align="right" sx={{ fontWeight: 'bold' }}>Email Address</TableCell>
          <TableCell align="right" sx={{ fontWeight: 'bold' }}>Phone Number</TableCell>
          <TableCell align="right" sx={{ fontWeight: 'bold' }}>Password</TableCell>
          <TableCell align="right" sx={{ fontWeight: 'bold' }}>Country</TableCell>
          <TableCell align="right" sx={{ fontWeight: 'bold' }}>Currency</TableCell>
          <TableCell align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
          <TableCell align="right" sx={{ fontWeight: 'bold' }}>Profile</TableCell>
          <TableCell align="right" sx={{ fontWeight: 'bold' }}>Registration Date</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {filteredUsersMemo.map((user) => (
          <TableRow
            key={user.id}
            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
          >
            <TableCell component="th" scope="row">
            {user.name}
            </TableCell>
            <TableCell align="right">{user.email}</TableCell>
            <TableCell align="right">{user.phone}</TableCell>
            <TableCell align="right">{user.pass_copy}</TableCell>
            <TableCell align="right">{user.country}</TableCell>
            <TableCell align="right">{user.currency}</TableCell>
            <TableCell align="right"><Button component={RouterLink} to={`/user-dashboard/${user.id}`} startIcon={<EditIcon />} size="small">View Dashboard</Button></TableCell>
            <TableCell align="right"><Avatar alt={user.name} src={user.profile} /></TableCell>
            <TableCell align="right">{getDate(user.created_at)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
    
    {/* Empty State */}
    {filteredUsersMemo.length === 0 && searchTerm && (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No users found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Try adjusting your search terms
        </Typography>
      </Box>
    )}
        </>
      )}
      
    </TableContainer>
    </Container>
    </div>
  );
}